import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { PostExpertBlock } from '../../components/PostExpertBlock';
import { parseExpertBlocks } from './parseExpertBlocks';
import { selectPostContentExpertBlocks } from '../../selectors/postContentExpertBlocks';
import { postContentExpertBlockSet, postContentExpertBlockReset } from '../../actions/postContentExpertBlock';
import { TPostContentExpertBlockTheme, TPostContentExpertBlockState } from '../../types/postContentExpertBlocks';

interface IUseContentParams {
  canParse: boolean;
  canRender: boolean;
  postContent: string;
  setPostContent: Dispatch<SetStateAction<string>>;
  done(): void;
}

/**
 * Рендерит и управляет Блоками эксперта
 */
export const useContentExpertBlocks = ({
  canParse,
  canRender,
  postContent,
  setPostContent,
  done,
}: IUseContentParams) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [prevPathname, setPrevPathname] = useState(pathname);
  const [isParsed, setIsParsed] = useState(false);
  const expertBlocks = useSelector(selectPostContentExpertBlocks);

  /** Сбрасывает стэйт при клиентском переходе с поста на пост */
  useEffect(() => {
    if (pathname !== prevPathname) {
      dispatch(postContentExpertBlockReset());
      setPrevPathname(pathname);
      setIsParsed(false);
    }
  }, [dispatch, pathname, prevPathname]);

  /** Рендерит Блоки эксперта */
  useEffect(() => {
    if (!expertBlocks.length || !canRender) {
      return;
    }

    expertBlocks.forEach(({ id, description, theme }: TPostContentExpertBlockState) => {
      const root = document.getElementById(id);

      if (root) {
        hydrateRoot(root, <PostExpertBlock text={description} theme={theme as TPostContentExpertBlockTheme} />);
      }
    });
  }, [expertBlocks, canRender]);

  /** Парсит параметры Цитат */
  useEffect(() => {
    if (!canParse || isParsed) {
      return;
    }

    if (expertBlocks.length) {
      setIsParsed(true);
      done();

      return;
    }

    const { items, html } = parseExpertBlocks(postContent, ({ id }) => `<div id="${id}"></div>`);

    if (items.length) {
      setPostContent(html);

      items.map(({ id, description, theme, position }) =>
        dispatch(
          postContentExpertBlockSet({
            id,
            description,
            theme: theme ? (theme as TPostContentExpertBlockTheme) : undefined,
            position,
          }),
        ),
      );
    }

    setIsParsed(true);
    done();
  }, [expertBlocks.length, canParse, dispatch, done, isParsed, postContent, setPostContent]);

  /** При размонтировании сбрасывает все Блоки эксперта */
  useEffect(() => {
    return () => {
      dispatch(postContentExpertBlockReset());
    };
  }, [dispatch]);
};
